import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";

import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Datatable from "../../../../core/common/dataTable";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import { PaginationType } from "../../../../types/common-type";

import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../../reusable/DynamicSingleSelect";

import TimelineModal from "../../../../core/common/TimelineModal";
import ModifyInviteModal from "../../../../core/common/ModifyInviteModal";

import {
  parseDate,
  parseDaysString,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";

import webservice_url from "../../../../core/webservice/Webservice_url";
import useApi from "../../../../core/customhook/useApi";
import Loading from "../../../../core/common/custom-loading/Loading";
import { formattedDate } from "../../../../core/methods/formattedDate";

import { DEFAULT_STATUS_STYLE, STATUS_STYLES } from "./TotalInviteSent";

import { getSearchableHeader } from "../../../../core/utils/getSearchableHeader";
import { exportAntdTableToExcel } from "../../../../core/utils/exportExcel";

type Props = {
  status_id?: string;
};

const PendingLogin: React.FC<Props> = ({ status_id }) => {
  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  // ============================================================
  // STATES
  // ============================================================

  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const [showModifyInvite, setShowModifyInvite] = useState(false);

  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);

  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

  const [allItemList, setAllItemList] = useState<any[]>([]);

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  // ============================================================
  // FILTERED TABLE DATA
  // ============================================================

  const filteredData = (Array.isArray(allItemList) ? allItemList : []).filter(
    (item: any) => {
      return Object.entries(inventoryColumnSearch).every(([key, value]) => {
        if (!value) return true;

        return String(item[key] ?? "")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
    },
  );

  // ============================================================
  // API
  // ============================================================

  const { callApi: searchCall, loading } = useApi<any>(
    webservice_url.admin_case_tracking_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;

    setShowTimeline(true);
    setTimelineLoading(true);

    try {
      const res = await getCaseLog.callApi({
        case_id: caseId,
      });

      setTimelineData(res?.data ?? []);
    } catch (error) {
      console.error("View log error:", error);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchData = async (
    filters: FilterTypes = filterData,
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
  ) => {
    try {
      const payload = {
        status: status_id ?? "",

        page,
        limit: pageSize,

        // Multi-select filter values
        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],
      };

      console.log("Pending Login payload:", payload);

      const res = await searchCall(payload);

      if (res?.success) {
        const tableData = Array.isArray(res?.data?.table) ? res.data.table : [];

        setAllItemList(tableData);

        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: Number(res?.data?.pagination?.total ?? 0),
        }));
      } else {
        setAllItemList([]);

        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error: any) {
      console.error("Pending Login API error:", error?.response || error);

      setAllItemList([]);

      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    }
  };

  const normalizeFilters = (storedFilters: any): FilterTypes => {
    return {
      organization: Array.isArray(storedFilters?.organization)
        ? storedFilters.organization
        : storedFilters?.organization
          ? [storedFilters.organization]
          : [],

      project: Array.isArray(storedFilters?.project)
        ? storedFilters.project
        : storedFilters?.project
          ? [storedFilters.project]
          : [],

      location: Array.isArray(storedFilters?.location)
        ? storedFilters.location
        : storedFilters?.location
          ? [storedFilters.location]
          : [],

      program: Array.isArray(storedFilters?.program)
        ? storedFilters.program
        : storedFilters?.program
          ? [storedFilters.program]
          : [],

      department: Array.isArray(storedFilters?.department)
        ? storedFilters.department
        : storedFilters?.department
          ? [storedFilters.department]
          : [],
    };
  };

  useEffect(() => {
    if (!status_id) return;

    const storedFilters = getLocalStorage("loginPending-candidate-tracking");

    const normalizedFilters = storedFilters
      ? normalizeFilters(storedFilters)
      : initialFilters;

    setFilterData(normalizedFilters);

    fetchData(normalizedFilters, 1, 10);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status_id]);

  useEffect(() => {
    if (pagination.current === 1 && pagination.pageSize === 10) {
      return;
    }

    fetchData(filterData, pagination.current, pagination.pageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  const handleFilterChange = (name: keyof FilterTypes, value: string[]) => {
    setFilterData((prev) => {
      const updated: FilterTypes = {
        ...prev,
        [name]: value,
      };

      // Organization changed
      // Clear all dependent filters
      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      // Project / Account changed
      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      // Location changed
      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      // Program changed
      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    // Clear validation error immediately
    // after user selects a value
    if (value?.length > 0) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    // Organization mandatory
    if (!filterData.organization?.length) {
      errors.organization = "Organization is required";
    }

    // Account / Project mandatory
    if (!filterData.project?.length) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    // Don't call API when validation fails
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Save filters
    setLocalStorage("loginPending-candidate-tracking", filterData);

    // Search should start from page 1
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already on page 1
    await fetchData(filterData, 1, pagination.pageSize);
  };

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});

    localStorage.removeItem("loginPending-candidate-tracking");

    // Reset pagination
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already on page 1
    await fetchData(clearedFilters, 1, pagination.pageSize);
  };

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },

    {
      title: getSearchableHeader(
        "Applicant Name",
        "candidate_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "candidate_name",
      key: "candidate_name",

      onCell: () => cellWidth(200),

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "candidate_name"),
    },

    {
      title: getSearchableHeader(
        "Case ID",
        "case_code",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_code",
      key: "case_code",

      onCell: () => cellWidth(200),

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "case_code"),
    },

    {
      title: getSearchableHeader(
        "Candidate Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),

      dataIndex: "hrms_id",
      key: "hrms_id",

      onCell: () => cellWidth(200),

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "hrms_id"),
    },
    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_spoc_name",
      key: "case_spoc_name",

      onCell: () => cellWidth(200),

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "case_spoc_name"),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "project_name",
      key: "project_name",

      onCell: () => cellWidth(200),

      width: "220px",

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "program_name",
      key: "program_name",

      onCell: () => cellWidth(200),

      width: "220px",

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "department_name",
      key: "department_name",

      onCell: () => cellWidth(200),

      width: "220px",

      render: (text: any) => text || "-",

      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: getSearchableHeader("Email ID", "email", setInventoryColumnSearch),

      dataIndex: "email",
      key: "email",

      onCell: () => cellWidth(200),

      render: (text: any) => (
        <div
          style={{
            wordBreak: "break-word",
          }}
        >
          {text || "-"}
        </div>
      ),

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "email"),
    },

    {
      title: getSearchableHeader(
        "Days Elapsed",
        "days_elapsed",
        setInventoryColumnSearch,
      ),

      dataIndex: "days_elapsed",
      key: "days_elapsed",

      align: "center",
      onCell: () => cellWidth(200),
      render: (text: any) => (text ? `${text} Days` : "-"),

      sorter: (a: any, b: any) =>
        parseDaysString(a.days_elapsed) - parseDaysString(b.days_elapsed),
    },

    {
      title: getSearchableHeader(
        "Phone No.",
        "phone",
        setInventoryColumnSearch,
      ),

      dataIndex: "phone",
      key: "phone",

      onCell: () => cellWidth(200),

      render: (text: any) => <div>{text || "-"}</div>,

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "phone"),
    },

    {
      title: getSearchableHeader(
        "Invitation Date",
        "invitation_date",
        setInventoryColumnSearch,
      ),

      dataIndex: "invitation_date",
      key: "invitation_date",

      onCell: () => cellWidth(200),

      render: (text: any) => <div>{text ? formattedDate(text) : "-"}</div>,

      sorter: (a: any, b: any) =>
        parseDate(a.invitation_date).getTime() -
        parseDate(b.invitation_date).getTime(),
    },

    {
      title: getSearchableHeader(
        "Status",
        "candidate_status",
        setInventoryColumnSearch,
      ),

      dataIndex: "candidate_status",
      key: "candidate_status",

      align: "center",

      onCell: () => cellWidth(200),

      render: (status: string) => {
        const { bg, color } =
          STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STATUS_STYLE;

        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{
              background: bg,
              color,
              whiteSpace: "nowrap",
            }}
          >
            <span
              className="rounded-circle"
              style={{
                width: 6,
                height: 6,
                background: color,
              }}
            />

            {status || "-"}
          </span>
        );
      },

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "candidate_status"),
    },

    {
      title: "View Log",
      key: "viewLog",

      align: "center",
      onCell: () => cellWidth(110),

      render: (_text: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{
            cursor: record?.case_id ? "pointer" : "not-allowed",
            opacity: record?.case_id ? 1 : 0.5,
          }}
          onClick={() => record?.case_id && handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye" />
        </div>
      ),
    },
  ];
  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Pending_Login_Report",
      sheetName: "Pending Login",
    });
  };

  return (
    <div className="px-2">
      <DynamicSingleSelect
        fields={[
          "organization",
          "project",
          "location",
          "program",
          "department",
        ]}
        filterData={filterData}
        onFilterDataChange={(name, value, _labels) => {
          handleFilterChange(name, value);
        }}
        parentError={filterErrors}
        showButton={true}
        buttonText="Search"
        onButtonClick={handleSearch}
        disableButton={loading}
        onClearAll={handleClearAll}
      />

      {/* ======================================================
          TABLE
      ======================================================= */}

      <div className="card mt-3">
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              rowKey="case_id"
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* ======================================================
          TIMELINE
      ======================================================= */}

      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}

      {/* ======================================================
          MODIFY INVITE
      ======================================================= */}

      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(data) => {
            console.log("Modify invite data:", data);

            setShowModifyInvite(false);
          }}
        />
      )}
    </div>
  );
};

export default PendingLogin;
